import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { StudioLayout } from "@/components/StudioLayout";
import { useBrandAccent } from "@/context/BrandContext";
import { Plus, X } from "lucide-react";
import { CHANNELS } from "@/lib/constants";
import { toast } from "sonner";

export default function Dashboard() {
  const [brands, setBrands] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const navigate = useNavigate();
  const { setAccent } = useBrandAccent();

  useEffect(() => { setAccent(null); load(); }, []);
  const load = () => api.get("/brands").then((r) => setBrands(r.data));

  const doDelete = async () => {
    try {
      await api.delete(`/brands/${confirmDelete.id}`);
      toast.success("Marca eliminada");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <StudioLayout>
      <div className="flex items-end justify-between mb-10">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl">Marcas</h1>
          <p className="text-subtle text-sm mt-1">El trabajo de cada cliente, en su propia pared.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="create-brand-btn" className="flex items-center gap-2 px-4 py-2.5 bg-ink text-white text-sm rounded-sm">
          <Plus size={16} /> Crear marca
        </button>
      </div>

      {brands === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => <div key={i} className="border border-line bg-surface aspect-[4/3] animate-pulse" />)}
        </div>
      ) : brands.length === 0 ? (
        <div className="border border-dashed border-line bg-surface py-20 text-center">
          <p className="font-display text-2xl mb-2">Aún no hay marcas</p>
          <p className="text-subtle text-sm mb-6">Crea la primera para empezar a planificar su contenido.</p>
          <button onClick={() => setCreating(true)} data-testid="empty-create-brand" className="px-4 py-2.5 bg-ink text-white text-sm rounded-sm">Crear marca</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {brands.map((b) => (
            <div
              key={b.id}
              onClick={() => navigate(`/brand/${b.id}`)}
              data-testid={`brand-card-${b.id}`}
              className="relative text-left border border-line bg-surface p-5 hover:border-ink transition-colors group cursor-pointer"
            >
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(b); }}
                data-testid={`delete-brand-${b.id}`}
                title="Eliminar marca"
                className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center border border-line bg-surface text-subtle hover:text-white hover:bg-changes hover:border-changes transition-colors z-10"
              >
                <X size={15} />
              </button>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-sm overflow-hidden border border-line bg-app flex items-center justify-center shrink-0">
                  {b.logo_url ? <img src={fileUrl(b.logo_url)} alt="" className="w-full h-full object-cover" /> :
                    <span className="font-display text-2xl" style={{ color: b.color }}>{b.name?.[0]}</span>}
                </div>
                <div className="flex-1 min-w-0 pr-6">
                  <div className="font-medium text-lg truncate">{b.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span style={{ width: 8, height: 8, background: b.color, borderRadius: "50%" }} />
                    <span className="font-data text-xs text-subtle">{b.color}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-6 mt-5 pt-4 border-t border-line">
                <Counter n={b.counters?.pending || 0} label="esperan respuesta" />
                <Counter n={b.counters?.changes || 0} label="con cambios" color="#A63D2E" />
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <CreateBrandModal onClose={() => setCreating(false)} onCreated={(b) => { setCreating(false); navigate(`/brand/${b.id}`); }} />}

      {confirmDelete && (
        <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-4 no-print" onClick={() => setConfirmDelete(null)}>
          <div className="bg-surface border border-line w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-brand-confirm">
            <h2 className="font-display text-2xl mb-2">Eliminar «{confirmDelete.name}»</h2>
            <p className="text-subtle text-sm mb-5">Se borrará la marca con todos sus posts, archivos, comentarios y accesos de cliente. Esta acción no se puede deshacer.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-brand" className="flex-1 py-2.5 border border-line text-sm rounded-sm">Cancelar</button>
              <button onClick={doDelete} data-testid="confirm-delete-brand" className="flex-1 py-2.5 text-white text-sm rounded-sm bg-changes">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}

function Counter({ n, label, color }) {
  return (
    <div>
      <div className="font-data text-2xl" style={{ color: n > 0 && color ? color : "#16161A" }}>{n}</div>
      <div className="text-xs text-subtle">{label}</div>
    </div>
  );
}

function CreateBrandModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", color: "#4A5568", timezone: "Europe/Madrid", channels: [], notes: "" });
  const [busy, setBusy] = useState(false);

  const toggleChannel = (key) => setForm((f) => ({ ...f, channels: f.channels.includes(key) ? f.channels.filter((c) => c !== key) : [...f.channels, key] }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Ponle un nombre a la marca."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/brands", form);
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/30 z-50 flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className="bg-surface border border-line w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-brand-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl">Nueva marca</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <L label="Nombre"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="brand-name-input" className="inp" /></L>
          <div className="flex gap-3">
            <L label="Color principal" className="flex-1">
              <div className="flex items-center gap-2">
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="brand-color-input" className="w-10 h-10 border border-line rounded-sm" />
                <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="inp font-data" />
              </div>
            </L>
          </div>
          <L label="Zona horaria"><input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="inp font-data" /></L>
          <div>
            <span className="text-sm text-subtle">Canales activos</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {CHANNELS.map((c) => (
                <button key={c.key} onClick={() => toggleChannel(c.key)} data-testid={`channel-${c.key}`}
                  className={`text-xs px-2.5 py-1.5 border rounded-sm ${form.channels.includes(c.key) ? "bg-ink text-white border-ink" : "border-line text-subtle"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={submit} disabled={busy} data-testid="submit-brand" className="w-full py-2.5 bg-ink text-white text-sm rounded-sm disabled:opacity-50">Crear marca</button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="text-sm text-subtle">{label}</span><div className="mt-1">{children}</div></label>;
}
